import React, { useState } from "react";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart3,
  Plus,
  Download,
  Filter,
  Calendar,
  CreditCard,
  Receipt,
  Banknote,
  Target,
  Wallet,
  FileText,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  Upload,
  Eye,
  Edit,
  Trash2,
  Send,
  RefreshCw,
  Shield,
  Globe,
  Smartphone,
  Building,
  Calculator,
  TrendingUp as TrendingUpIcon,
  Activity,
} from "lucide-react";
import { useData } from "../context/DataContext";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Table from "../components/ui/Table";
import Modal from "../components/ui/Modal";
import Badge from "../components/ui/Badge";
import { format } from "date-fns";

export default function Accounting() {
  const { state, dispatch } = useData();
  const [activeTab, setActiveTab] = useState<
    "overview" | "transactions" | "budget" | "reports" | "payments" | "payroll"
  >("overview");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [dateRange, setDateRange] = useState("month");
  const [searchTerm, setSearchTerm] = useState("");

  // Calculate financial metrics
  const totalIncome = state.financialRecords
    .filter((r) => r.type === "income")
    .reduce((sum, r) => sum + r.amount, 0);

  const totalExpenses = state.financialRecords
    .filter((r) => r.type === "expense")
    .reduce((sum, r) => sum + r.amount, 0);

  const netIncome = totalIncome - totalExpenses;

  const monthlyIncome = state.financialRecords
    .filter(
      (r) =>
        r.type === "income" &&
        new Date(r.date).getMonth() === new Date().getMonth()
    )
    .reduce((sum, r) => sum + r.amount, 0);

  const monthlyExpenses = state.financialRecords
    .filter(
      (r) =>
        r.type === "expense" &&
        new Date(r.date).getMonth() === new Date().getMonth()
    )
    .reduce((sum, r) => sum + r.amount, 0);

  // Sample data for new features
  const paymentMethods = [
    {
      id: 1,
      name: "Credit Card",
      type: "card",
      status: "active",
      transactions: 245,
      amount: 125000,
    },
    {
      id: 2,
      name: "Bank Transfer",
      type: "transfer",
      status: "active",
      transactions: 89,
      amount: 450000,
    },
    {
      id: 3,
      name: "Mobile Wallet",
      type: "mobile",
      status: "active",
      transactions: 156,
      amount: 78000,
    },
    {
      id: 4,
      name: "Cash",
      type: "cash",
      status: "active",
      transactions: 45,
      amount: 12000,
    },
  ];

  const pendingPayments = [
    {
      id: 1,
      member: "Ahmed Hassan",
      amount: 5000,
      dueDate: "2024-01-15",
      status: "overdue",
      method: "Credit Card",
    },
    {
      id: 2,
      member: "Sara Mohamed",
      amount: 3000,
      dueDate: "2024-01-20",
      status: "pending",
      method: "Bank Transfer",
    },
    {
      id: 3,
      member: "Omar Ali",
      amount: 7500,
      dueDate: "2024-01-25",
      status: "pending",
      method: "Mobile Wallet",
    },
  ];

  const payrollData = [
    {
      id: 1,
      employee: "Fatma Ibrahim",
      position: "Secretary",
      salary: 8000,
      status: "paid",
      payDate: "2024-01-01",
    },
    {
      id: 2,
      employee: "Mohamed Hassan",
      position: "Accountant",
      salary: 12000,
      status: "paid",
      payDate: "2024-01-01",
    },
    {
      id: 3,
      employee: "Aisha Ahmed",
      position: "Legal Advisor",
      salary: 15000,
      status: "pending",
      payDate: "2024-01-15",
    },
  ];

  const handleAddTransaction = (transactionData: any) => {
    const newTransaction = {
      ...transactionData,
      id: Date.now().toString(),
    };
    dispatch({ type: "ADD_FINANCIAL_RECORD", payload: newTransaction });
    setShowAddModal(false);
  };

  const handleCreateBudget = (budgetData: any) => {
    // Simulate budget creation
    // Creating budget
    setShowBudgetModal(false);
  };

  const handleProcessPayment = (paymentData: any) => {
    // Simulate payment processing
    // Processing payment
    setShowPaymentModal(false);
  };

  const handleProcessPayroll = (payrollData: any) => {
    // Simulate payroll processing
    // Processing payroll
    setShowPayrollModal(false);
  };

  const getTypeBadge = (type: string) => {
    return type === "income" ? (
      <Badge variant="success">Income</Badge>
    ) : (
      <Badge variant="error">Expense</Badge>
    );
  };

  const transactionColumns = [
    {
      key: "date",
      header: "Date",
      render: (item: any) => format(new Date(item.date), "MMM dd, yyyy"),
    },
    {
      key: "type",
      header: "Type",
      render: (item: any) => getTypeBadge(item.type),
    },
    {
      key: "category",
      header: "Category",
      render: (item: any) => item.category,
    },
    {
      key: "description",
      header: "Description",
      render: (item: any) => item.description,
    },
    {
      key: "amount",
      header: "Amount",
      render: (item: any) => (
        <span
          className={`font-medium ${
            item.type === "income" ? "text-green-600" : "text-red-600"
          }`}
        >
          {item.type === "income" ? "+" : "-"}EGP {item.amount.toLocaleString()}
        </span>
      ),
    },
    {
      key: "reference",
      header: "Reference",
      render: (item: any) => item.reference || "-",
    },
  ];

  const budgetCategories = [
    {
      category: "Membership Fees",
      budgeted: 500000,
      actual: 450000,
      variance: -50000,
    },
    { category: "Office Rent", budgeted: 180000, actual: 180000, variance: 0 },
    {
      category: "Staff Salaries",
      budgeted: 300000,
      actual: 320000,
      variance: 20000,
    },
    { category: "Utilities", budgeted: 50000, actual: 45000, variance: -5000 },
    {
      category: "Legal Fees",
      budgeted: 100000,
      actual: 120000,
      variance: 20000,
    },
    {
      category: "Events & Activities",
      budgeted: 200000,
      actual: 180000,
      variance: -20000,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Accounting & Finance
          </h1>
          <p className="text-gray-600">
            Financial management, payments, and reporting
          </p>
        </div>
        <div className="flex gap-2">
          {activeTab === "overview" && (
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Transaction
            </Button>
          )}
          {activeTab === "budget" && (
            <Button onClick={() => setShowBudgetModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Budget
            </Button>
          )}
          {activeTab === "payments" && (
            <Button onClick={() => setShowPaymentModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Process Payment
            </Button>
          )}
          {activeTab === "payroll" && (
            <Button onClick={() => setShowPayrollModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Process Payroll
            </Button>
          )}
        </div>
      </div>

      {/* Financial Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Income</p>
              <p className="text-2xl font-bold text-green-600">
                EGP {totalIncome.toLocaleString()}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <TrendingDown className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Expenses
              </p>
              <p className="text-2xl font-bold text-red-600">
                EGP {totalExpenses.toLocaleString()}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Net Income</p>
              <p
                className={`text-2xl font-bold ${
                  netIncome >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                EGP {netIncome.toLocaleString()}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Target className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Monthly Budget
              </p>
              <p className="text-2xl font-bold text-purple-600">
                EGP 1,200,000
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "overview", name: "Overview", icon: PieChart },
            { id: "transactions", name: "Transactions", icon: Receipt },
            { id: "budget", name: "Budget", icon: Target },
            { id: "payments", name: "Payments", icon: CreditCard },
            { id: "payroll", name: "Payroll", icon: Users },
            { id: "reports", name: "Reports", icon: BarChart3 },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Income vs Expenses Chart */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Monthly Overview
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                  <span className="text-sm text-gray-600">Income</span>
                </div>
                <span className="font-semibold text-green-600">
                  EGP {monthlyIncome.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
                  <span className="text-sm text-gray-600">Expenses</span>
                </div>
                <span className="font-semibold text-red-600">
                  EGP {monthlyExpenses.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{
                    width: `${
                      (monthlyIncome / (monthlyIncome + monthlyExpenses)) * 100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          </Card>

          {/* Recent Transactions */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Recent Transactions
            </h3>
            <div className="space-y-3">
              {state.financialRecords.slice(0, 5).map((record) => (
                <div
                  key={record.id}
                  className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {record.description}
                    </div>
                    <div className="text-sm text-gray-500">
                      {record.category}
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className={`text-sm font-medium ${
                        record.type === "income"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {record.type === "income" ? "+" : "-"}EGP{" "}
                      {record.amount.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500">
                      {format(new Date(record.date), "MMM dd")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {activeTab === "transactions" && (
        <div className="space-y-6">
          {/* Filters */}
          <Card>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input placeholder="Search transactions..." />
              </div>
              <div className="flex gap-2">
                <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option>All Types</option>
                  <option>Income</option>
                  <option>Expense</option>
                </select>
                <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option>All Categories</option>
                  <option>Membership Fees</option>
                  <option>Office Rent</option>
                  <option>Staff Salaries</option>
                </select>
                <Button variant="outline">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </Card>

          {/* Transactions Table */}
          <Card padding="none">
            <Table
              data={state.financialRecords}
              columns={transactionColumns}
              emptyMessage="No transactions found"
            />
          </Card>
        </div>
      )}

      {activeTab === "budget" && (
        <div className="space-y-6">
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Budget vs Actual
            </h3>
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Budgeted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actual
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Variance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {budgetCategories.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.category}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        EGP {item.budgeted.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        EGP {item.actual.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={
                            item.variance >= 0
                              ? "text-green-600"
                              : "text-red-600"
                          }
                        >
                          {item.variance >= 0 ? "+" : ""}EGP{" "}
                          {item.variance.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item.variance === 0 ? (
                          <Badge variant="success">On Budget</Badge>
                        ) : item.variance < 0 ? (
                          <Badge variant="success">Under Budget</Badge>
                        ) : (
                          <Badge variant="warning">Over Budget</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {activeTab === "payments" && (
        <div className="space-y-6">
          {/* Payment Methods Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {paymentMethods.map((method) => (
              <Card key={method.id}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {method.name}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      EGP {method.amount.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">
                      {method.transactions} transactions
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg">
                    {method.type === "card" && (
                      <CreditCard className="h-6 w-6 text-green-600" />
                    )}
                    {method.type === "transfer" && (
                      <Building className="h-6 w-6 text-green-600" />
                    )}
                    {method.type === "mobile" && (
                      <Smartphone className="h-6 w-6 text-green-600" />
                    )}
                    {method.type === "cash" && (
                      <Banknote className="h-6 w-6 text-green-600" />
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Pending Payments */}
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Pending Payments
              </h3>
              <Button variant="outline">
                <Send className="h-4 w-4 mr-2" />
                Send Reminders
              </Button>
            </div>
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Member
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment.member}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        EGP {payment.amount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(payment.dueDate), "MMM dd, yyyy")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.method}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payment.status === "overdue" ? (
                          <Badge variant="error">Overdue</Badge>
                        ) : (
                          <Badge variant="warning">Pending</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="sm">
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {activeTab === "payroll" && (
        <div className="space-y-6">
          {/* Payroll Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Total Employees
                  </p>
                  <p className="text-2xl font-bold text-blue-600">3</p>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Paid This Month
                  </p>
                  <p className="text-2xl font-bold text-green-600">2</p>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Clock className="h-6 w-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-orange-600">1</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Payroll Table */}
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Employee Payroll
              </h3>
              <div className="flex gap-2">
                <Button variant="outline">
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculate
                </Button>
                <Button>
                  <Send className="h-4 w-4 mr-2" />
                  Process All
                </Button>
              </div>
            </div>
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Position
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Salary
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pay Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payrollData.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {employee.employee}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {employee.position}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        EGP {employee.salary.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {employee.status === "paid" ? (
                          <Badge variant="success">Paid</Badge>
                        ) : (
                          <Badge variant="warning">Pending</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(employee.payDate), "MMM dd, yyyy")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline">
                            <Eye className="h-4 w-4" />
                          </Button>
                          {employee.status === "pending" && (
                            <Button size="sm">
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {activeTab === "reports" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Reports
            </h3>
            <div className="space-y-3">
              <Button variant="outline" className="w-full justify-start">
                <Download className="h-4 w-4 mr-2" />
                Income Statement
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Download className="h-4 w-4 mr-2" />
                Balance Sheet
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Download className="h-4 w-4 mr-2" />
                Cash Flow Statement
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Download className="h-4 w-4 mr-2" />
                Budget Variance Report
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Download className="h-4 w-4 mr-2" />
                Payment Summary
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Download className="h-4 w-4 mr-2" />
                Payroll Report
              </Button>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Custom Reports
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date Range
                </label>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="quarter">This Quarter</option>
                  <option value="year">This Year</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Report Type
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="financial">Financial Summary</option>
                  <option value="payments">Payment Analysis</option>
                  <option value="payroll">Payroll Summary</option>
                  <option value="budget">Budget Analysis</option>
                </select>
              </div>
              <Button className="w-full">
                <BarChart3 className="h-4 w-4 mr-2" />
                Generate Report
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modals */}
      <AddTransactionModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddTransaction}
      />
      <BudgetModal
        isOpen={showBudgetModal}
        onClose={() => setShowBudgetModal(false)}
        onCreate={handleCreateBudget}
      />
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onProcess={handleProcessPayment}
      />
      <PayrollModal
        isOpen={showPayrollModal}
        onClose={() => setShowPayrollModal(false)}
        onProcess={handleProcessPayroll}
      />
    </div>
  );
}

// Add Transaction Modal Component
function AddTransactionModal({
  isOpen,
  onClose,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (transaction: any) => void;
}) {
  const [formData, setFormData] = useState({
    type: "income" as const,
    category: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    description: "",
    reference: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd({
      ...formData,
      amount: parseFloat(formData.amount),
    });
    setFormData({
      type: "income",
      category: "",
      amount: "",
      date: new Date().toISOString().split("T")[0],
      description: "",
      reference: "",
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Transaction" size="md">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaction Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="income">Income</option>
              <option value="expense">Expense</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select Category</option>
              <option value="Membership Fees">Membership Fees</option>
              <option value="Office Rent">Office Rent</option>
              <option value="Staff Salaries">Staff Salaries</option>
              <option value="Utilities">Utilities</option>
              <option value="Legal Fees">Legal Fees</option>
              <option value="Events & Activities">Events & Activities</option>
            </select>
          </div>
          <Input
            label="Amount (EGP)"
            type="number"
            value={formData.amount}
            onChange={(e) =>
              setFormData({ ...formData, amount: e.target.value })
            }
            required
          />
          <Input
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
          />
        </div>
        <Input
          label="Description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          required
        />
        <Input
          label="Reference (Optional)"
          value={formData.reference}
          onChange={(e) =>
            setFormData({ ...formData, reference: e.target.value })
          }
        />

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Add Transaction</Button>
        </div>
      </form>
    </Modal>
  );
}

// Budget Modal Component
function BudgetModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (budget: any) => void;
}) {
  const [formData, setFormData] = useState({
    category: "",
    budgetedAmount: "",
    period: "monthly",
    description: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({
      ...formData,
      budgetedAmount: parseFloat(formData.budgetedAmount),
    });
    setFormData({
      category: "",
      budgetedAmount: "",
      period: "monthly",
      description: "",
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Budget" size="md">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            value={formData.category}
            onChange={(e) =>
              setFormData({ ...formData, category: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          >
            <option value="">Select Category</option>
            <option value="Membership Fees">Membership Fees</option>
            <option value="Office Rent">Office Rent</option>
            <option value="Staff Salaries">Staff Salaries</option>
            <option value="Utilities">Utilities</option>
            <option value="Legal Fees">Legal Fees</option>
            <option value="Events & Activities">Events & Activities</option>
          </select>
        </div>
        <Input
          label="Budgeted Amount (EGP)"
          type="number"
          value={formData.budgetedAmount}
          onChange={(e) =>
            setFormData({ ...formData, budgetedAmount: e.target.value })
          }
          required
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Period
          </label>
          <select
            value={formData.period}
            onChange={(e) =>
              setFormData({ ...formData, period: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
        <Input
          label="Description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
        />

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Create Budget</Button>
        </div>
      </form>
    </Modal>
  );
}

// Payment Modal Component
function PaymentModal({
  isOpen,
  onClose,
  onProcess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onProcess: (payment: any) => void;
}) {
  const [formData, setFormData] = useState({
    memberId: "",
    amount: "",
    method: "credit_card",
    description: "",
    reference: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onProcess({
      ...formData,
      amount: parseFloat(formData.amount),
    });
    setFormData({
      memberId: "",
      amount: "",
      method: "credit_card",
      description: "",
      reference: "",
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Process Payment" size="md">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Member
          </label>
          <select
            value={formData.memberId}
            onChange={(e) =>
              setFormData({ ...formData, memberId: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          >
            <option value="">Select Member</option>
            <option value="1">Ahmed Hassan</option>
            <option value="2">Sara Mohamed</option>
            <option value="3">Omar Ali</option>
          </select>
        </div>
        <Input
          label="Amount (EGP)"
          type="number"
          value={formData.amount}
          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
          required
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Payment Method
          </label>
          <select
            value={formData.method}
            onChange={(e) =>
              setFormData({ ...formData, method: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="credit_card">Credit Card</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="mobile_wallet">Mobile Wallet</option>
            <option value="cash">Cash</option>
          </select>
        </div>
        <Input
          label="Description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
        />
        <Input
          label="Reference (Optional)"
          value={formData.reference}
          onChange={(e) =>
            setFormData({ ...formData, reference: e.target.value })
          }
        />

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Process Payment</Button>
        </div>
      </form>
    </Modal>
  );
}

// Payroll Modal Component
function PayrollModal({
  isOpen,
  onClose,
  onProcess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onProcess: (payroll: any) => void;
}) {
  const [formData, setFormData] = useState({
    employeeId: "",
    salary: "",
    bonus: "",
    deductions: "",
    payDate: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onProcess({
      ...formData,
      salary: parseFloat(formData.salary),
      bonus: parseFloat(formData.bonus) || 0,
      deductions: parseFloat(formData.deductions) || 0,
    });
    setFormData({
      employeeId: "",
      salary: "",
      bonus: "",
      deductions: "",
      payDate: new Date().toISOString().split("T")[0],
      notes: "",
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Process Payroll" size="md">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Employee
          </label>
          <select
            value={formData.employeeId}
            onChange={(e) =>
              setFormData({ ...formData, employeeId: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          >
            <option value="">Select Employee</option>
            <option value="1">Fatma Ibrahim - Secretary</option>
            <option value="2">Mohamed Hassan - Accountant</option>
            <option value="3">Aisha Ahmed - Legal Advisor</option>
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Base Salary (EGP)"
            type="number"
            value={formData.salary}
            onChange={(e) =>
              setFormData({ ...formData, salary: e.target.value })
            }
            required
          />
          <Input
            label="Bonus (EGP)"
            type="number"
            value={formData.bonus}
            onChange={(e) =>
              setFormData({ ...formData, bonus: e.target.value })
            }
          />
          <Input
            label="Deductions (EGP)"
            type="number"
            value={formData.deductions}
            onChange={(e) =>
              setFormData({ ...formData, deductions: e.target.value })
            }
          />
          <Input
            label="Pay Date"
            type="date"
            value={formData.payDate}
            onChange={(e) =>
              setFormData({ ...formData, payDate: e.target.value })
            }
            required
          />
        </div>
        <Input
          label="Notes (Optional)"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        />

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Process Payroll</Button>
        </div>
      </form>
    </Modal>
  );
}
